/**
 * Pure logic for the charger history page (/history/wifi_switch/<YYYYMMDD>).
 *
 * Mirrors the semantics of wifi_switch/tools/crg_report.py, with one
 * deliberate difference: gap detection is local, because a day can mix 5 s and
 * 30 s rows (a single day-wide median would call every 30 s row a gap).
 * No Firebase in here -- the page fetches, this file interprets.
 */

const WIB_OFFSET_S = 7 * 3600;
/** Boot time (epoch - up) may drift this much between rows before it is a reboot. */
const BOOT_JITTER_S = 10;
const GAP_FLOOR_S = 15;
const GAP_FACTOR = 2.5;
/** How many neighbouring deltas on each side define the "local" row spacing. */
const SPACING_WINDOW = 10;

/** DS18B20 "sensor disconnected" reading (x10 on the wire), never a temperature. */
export const TEMP_DISCONNECTED = -1270;

/** Panel voltage saturates here: the ADC limit, not a measurement. */
export const PANEL_RAILED_MV = 20729;

export interface Row {
  /** Unix seconds (UTC). */
  t: number;
  v?: number;
  s?: number;
  ma?: number;
  g?: number;
  d?: number;
  o?: number;
  c?: number;
  b?: number;
  r?: number;
  e?: number;
  om?: number;
  up?: number;
  rr?: number;
  xr?: number;
  xc?: number;
  xp?: string;
  xv?: string;
  xs?: string;
  h?: number;
  k?: number;
  /** teras: pack mV, board-voltage peak/valley, probe-ON peak, current peak/valley, lamp duty, pump bitmask. */
  p?: number;
  vx?: number;
  vn?: number;
  px?: number;
  mx?: number;
  mn?: number;
  l?: number;
  pm?: number;
  /** u1..uN (charger) or t1..tN (teras) in °C (the wire value is x10). */
  temps: Record<string, number>;
  /** teras rh1..: relative humidity in whole percent (not x10). Absent when none. */
  hum?: Record<string, number>;
}

export interface Crash {
  xr?: number;
  xc?: number;
  xp?: string;
  xv?: string;
  xs?: string;
}

export interface Reboot {
  index: number;
  t: number;
  rr?: number;
  crash?: Crash;
}

export interface StageSegment {
  g: number;
  start: number;
  end: number;
}

export type CounterKey = "b" | "r" | "e" | "om";
export interface CounterRise {
  t: number;
  key: CounterKey;
  delta: number;
}

const NUM_KEYS = [
  "v", "s", "ma", "g", "d", "o", "c", "b", "r", "e", "om", "up", "rr", "xr", "xc", "h", "k",
  "p", "vx", "vn", "px", "mx", "mn", "l", "pm",
] as const;
const STR_KEYS = ["xp", "xv", "xs"] as const;

const num = (x: unknown): number | undefined =>
  typeof x === "number" && Number.isFinite(x) ? x : undefined;

/** RTDB `{ "<epoch>": row }` -> rows sorted by time. Null/garbage tolerant. */
export function parseDay(day: unknown): Row[] {
  if (!day || typeof day !== "object") return [];
  const out: Row[] = [];
  for (const [k, raw] of Object.entries(day as Record<string, unknown>)) {
    if (!/^\d+$/.test(k) || !raw || typeof raw !== "object") continue;
    const src = raw as Record<string, unknown>;
    const row: Row = { t: Number(k), temps: {} };
    for (const key of NUM_KEYS) {
      const n = num(src[key]);
      if (n !== undefined) row[key] = n;
    }
    for (const key of STR_KEYS) {
      const val = src[key];
      if (typeof val === "string") row[key] = val;
      else if (typeof val === "number") row[key] = val.toString(16);
    }
    for (const [key, val] of Object.entries(src)) {
      const n = num(val);
      if (/^rh\d+$/.test(key) && n !== undefined) (row.hum ??= {})[key] = n;
      // A disconnected sensor is "no data", not -127 °C: it would wreck the scale.
      if (/^[ut]\d+$/.test(key) && n !== undefined && n !== TEMP_DISCONNECTED)
        row.temps[key] = n / 10;
    }
    out.push(row);
  }
  return out.sort((a, b) => a.t - b.t);
}

function median(xs: number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const m = s.length >> 1;
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

/**
 * Indices `i` whose row is not contiguous with row `i-1`: the delta exceeds
 * 2.5x the local spacing. "Local" is the larger of the median delta to the left
 * and to the right, so the 5 s -> 30 s switch is not a gap but a hole inside a
 * 5 s stretch still is.
 */
export function findGaps(rows: Row[]): number[] {
  const n = rows.length;
  if (n < 2) return [];
  const delta: number[] = [];
  for (let i = 1; i < n; i++) delta.push(rows[i].t - rows[i - 1].t);
  const gaps: number[] = [];
  for (let i = 1; i < n; i++) {
    const j = i - 1; // delta index of this pair
    const left = delta.slice(Math.max(0, j - SPACING_WINDOW), j);
    const right = delta.slice(j + 1, j + 1 + SPACING_WINDOW);
    const local = Math.max(
      left.length ? median(left) : 0,
      right.length ? median(right) : 0,
      // Two-row day: nothing around to compare with.
      left.length || right.length ? 0 : delta[j]
    );
    if (delta[j] > Math.max(GAP_FLOOR_S, GAP_FACTOR * local)) gaps.push(i);
  }
  return gaps;
}

const bootEpoch = (r: Row) => r.t - (r.up as number);

function crashOf(r: Row): Crash | undefined {
  if (r.xr === undefined) return undefined;
  return { xr: r.xr, xc: r.xc, xp: r.xp, xv: r.xv, xs: r.xs };
}

/**
 * Reboot = `up` fell, or boot time (epoch - up) jumped forward by > 10 s (a
 * short boot followed by a slow one leaves `up` rising). Rows without `up` are
 * skipped, never counted as boot-at-0 -- that would flag every row.
 * A crash record on the very first row is reported at index 0: that boot
 * started before today but its crash note is still worth showing.
 */
export function findReboots(rows: Row[]): Reboot[] {
  const out: Reboot[] = [];
  if (rows.length && rows[0].xr !== undefined)
    out.push({ index: 0, t: rows[0].t, rr: rows[0].rr, crash: crashOf(rows[0]) });
  let last: Row | undefined;
  rows.forEach((r, index) => {
    if (r.up === undefined) return;
    if (
      last &&
      (r.up < (last.up as number) || bootEpoch(r) - bootEpoch(last) > BOOT_JITTER_S) &&
      index > 0
    )
      out.push({ index, t: r.t, rr: r.rr, crash: crashOf(r) });
    last = r;
  });
  return out;
}

/**
 * Stage runs for the background bands. A run ends where the next begins, or at
 * its last row before a gap -- the gap itself is not coloured.
 */
export function stageSegments(rows: Row[], gaps: number[]): StageSegment[] {
  const gapSet = new Set(gaps);
  const segs: StageSegment[] = [];
  let cur: StageSegment | undefined;
  rows.forEach((r, i) => {
    if (r.g === undefined) return;
    if (cur && !gapSet.has(i)) {
      cur.end = r.t;
      if (cur.g === r.g) return;
    }
    cur = { g: r.g, start: r.t, end: r.t };
    segs.push(cur);
  });
  return segs;
}

const midnightWib = (t: number) =>
  Math.floor((t + WIB_OFFSET_S) / 86400) * 86400 - WIB_OFFSET_S;

/**
 * Where the cumulative counters (b, r, e, om) went up. Counters restart at 0 on
 * every reboot, so the baseline is per boot. A boot that started today counts
 * from 0 (events before the first row were still real); one that started
 * earlier is measured from its first row here.
 */
export function counterRises(rows: Row[]): CounterRise[] {
  const out: CounterRise[] = [];
  const keys: CounterKey[] = ["b", "r", "e", "om"];
  const bootStarts = new Set(findReboots(rows).map((r) => r.index));
  let prev: Partial<Record<CounterKey, number>> = {};
  rows.forEach((row, i) => {
    if (i === 0 || bootStarts.has(i)) {
      const fromZero = row.up !== undefined && bootEpoch(row) >= midnightWib(row.t);
      prev = fromZero ? { b: 0, r: 0, e: 0, om: 0 } : {};
    }
    for (const k of keys) {
      const x = row[k];
      if (x === undefined) continue;
      const p = prev[k];
      if (p !== undefined && x > p) out.push({ t: row.t, key: k, delta: x - p });
      prev[k] = x;
    }
  });
  return out;
}

/**
 * Indices to draw: a regular stride, plus the first/last and any `force`d
 * index (events, gap edges) so downsampling never hides one.
 */
export function downsample(n: number, max: number, force: Iterable<number>): number[] {
  if (n <= max) return Array.from({ length: n }, (_, i) => i);
  const stride = Math.ceil(n / max);
  const keep = new Set<number>([0, n - 1]);
  for (let i = 0; i < n; i += stride) keep.add(i);
  Array.from(force).forEach((f) => f >= 0 && f < n && keep.add(f));
  return Array.from(keep).sort((a, b) => a - b);
}

/** Today (WIB) and the six days before it, newest first: "YYYYMMDD". */
export function dayList(nowMs: number): string[] {
  const w = new Date(nowMs + WIB_OFFSET_S * 1000);
  const y = w.getUTCFullYear();
  const m = w.getUTCMonth();
  const d = w.getUTCDate();
  return Array.from({ length: 7 }, (_, i) => {
    const x = new Date(Date.UTC(y, m, d - i));
    return (
      x.getUTCFullYear().toString() +
      String(x.getUTCMonth() + 1).padStart(2, "0") +
      String(x.getUTCDate()).padStart(2, "0")
    );
  });
}

/** WIB midnight of "YYYYMMDD", in Unix seconds. */
export function dayStart(day: string): number {
  const y = Number(day.slice(0, 4));
  const m = Number(day.slice(4, 6));
  const d = Number(day.slice(6, 8));
  return Date.UTC(y, m - 1, d) / 1000 - WIB_OFFSET_S;
}

const p2 = (n: number) => String(n).padStart(2, "0");

/** "HH:MM:SS" in WIB, independent of the browser's time zone. */
export function wibClock(epoch: number): string {
  const x = new Date((epoch + WIB_OFFSET_S) * 1000);
  return `${p2(x.getUTCHours())}:${p2(x.getUTCMinutes())}:${p2(x.getUTCSeconds())}`;
}

/** Axis ticks for [a, b]: a step giving <= 9 ticks, aligned on WIB wall-clock. */
export function ticksFor(a: number, b: number): number[] {
  const steps = [60, 300, 600, 1800, 3600, 7200, 10800, 21600];
  const step = steps.find((s) => (b - a) / s <= 9) ?? 21600;
  const off = 7 * 3600; // align on WIB wall-clock, not UTC
  const out: number[] = [];
  for (let t = Math.ceil((a + off) / step) * step - off; t <= b; t += step) out.push(t);
  return out;
}
